/**
 * TypeScript types for the WebSocket protocol and API responses.
 */

/** Parse a DB timestamp as UTC. SQLite stores UTC but strips the timezone suffix. */
export function parseUTC(ts: string): Date {
  if (!ts) return new Date(NaN);
  // If it already has timezone info (Z or +00:00), parse as-is
  if (ts.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(ts)) return new Date(ts);
  // Otherwise append Z so JS treats it as UTC, not local time
  return new Date(ts.replace(" ", "T") + "Z");
}

// --- Auth ---

export interface User {
  id: string;
  email: string;
  name: string;
  avatar_url: string | null;
  role?: string | null;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

// --- Conversations ---

export interface Conversation {
  id: string;
  title: string;
  workflow?: "auto" | "general" | "shortlist";
  status: "active" | "completed" | "archived";
  /** Size of the last turn's prompt in tokens (input + cache reads). */
  context_tokens?: number | null;
  /** Model that produced the above reading — used to pick the correct limit. */
  context_model?: string | null;
  /** The user's model choice for this conversation: the sentinel ``"auto"``
   *  or a concrete model id. Distinct from ``context_model`` (the resolved
   *  model). The model picker restores this on reopen; ``null`` reads as
   *  ``"auto"``. Models are per-conversation — there is no global default. */
  selected_model?: string | null;
  /** External MCP connectors (mcp_servers.id) opted into for THIS conversation.
   *  Settings enable = "connect & load tools"; this chat-level list = "include
   *  those tools in this chat". Internal MCPs (cowork tools + Nbyula gateway)
   *  are always on and not listed. Hydrated on open; ``null``/absent → none. */
  enabled_connectors?: string[] | null;
  /** Where this conversation's turns run (ADR-0008 location awareness):
   *  ``"cloud"`` or ``"desktop:<device_id>"``. ``null`` for pre-sync chats. */
  execution_location?: string | null;
  /** Whether an agent turn is running RIGHT NOW — derived server-side from the
   *  durable v30 row stamps (start + fresh heartbeat), so every window on every
   *  device gets the same answer (#79). SQLite boolean: 0/1. */
  agent_running?: number | boolean | null;
  /** When the running turn started (ISO, UTC) — anchors the elapsed timer for a
   *  turn running on another environment. NULL when not running. */
  active_turn_started_at?: string | null;
  /** Whether this chat is BLOCKED ON THE USER right now (v38, ADR-0017) — an
   *  AskUserQuestion widget or a tool-approval request is outstanding. Derived
   *  server-side and replicated like ``agent_running``, so the sidebar's
   *  Working/Needs Input split is identical on every device. SQLite boolean: 0/1.
   *
   *  Takes PRECEDENCE over ``agent_running``, which stays true throughout: a
   *  blocked turn still holds its lease (only the terminal ``result`` row
   *  releases it), so a chat waiting on you is BOTH running and blocked. The
   *  actionable half is the one that should decide which section it lands in. */
  needs_input?: number | boolean | null;
  /** When the user was first blocked (ISO, UTC) — orders the "Needs Input"
   *  section by who has been waiting longest. NULL when nothing is blocking. */
  needs_input_at?: string | null;
  /** How many sub-agent runs on this chat can still report back (ADR-0019) —
   *  the replicated summary of ``subagent_runs``, maintained in the same
   *  transaction as the rows it counts.
   *
   *  Keeps a chat in "Working" between turns. A delegated run outlives the turn
   *  that launched it, and the lease is released by that turn's terminal row, so
   *  ``agent_running`` alone files an actively-working chat under "Completed" —
   *  the same "the spinner and the truth disagree" class ADR-0015 fixed for
   *  turns, one layer up. */
  open_subagents?: number | null;
  /** One-line "where this chat is at", regenerated server-side at the end of
   *  every turn (v39, ADR-0018) and rendered under the title in the sidebar.
   *  NULL for chats that haven't had a turn since the feature landed — the row
   *  then renders exactly as it did before, title only. */
  recap?: string | null;
  /** Cumulative fresh input tokens (non-cached) across the conversation. */
  input_tokens?: number | null;
  /** Cumulative tokens the model has emitted. */
  output_tokens?: number | null;
  /** Cumulative tokens served from prompt cache (billed cheaper). */
  cache_read_tokens?: number | null;
  /** Cumulative tokens written to prompt cache. */
  cache_creation_tokens?: number | null;
  /** Cumulative model spend (Claude / OpenRouter) for the conversation, USD. */
  cost_usd?: number | null;
  /** Cumulative Perplexity tool spend for the conversation, USD. */
  pplx_cost_usd?: number | null;
  /** Per-model / per-provider token breakdown — the cumulative
   *  ``input``/``output``/``cache`` buckets split by the model that ran each
   *  turn, tagged with its provider. Returned already parsed (the backend
   *  stores it as JSON TEXT). Defaults to ``{}`` when no costed turn has run
   *  yet. Sibling of the flat ``*_tokens`` totals above, which stay the grand
   *  total across all models. */
  model_token_usage?: ModelTokenUsage | null;
  /** The CLI's own measurement of the last turn's context window (v36) —
   *  what `/context` shows. `null` until a turn produces one. */
  context_breakdown?: ContextBreakdown | null;
  /** ISO timestamp set when the user pins the chat; null when unpinned. */
  pinned_at?: string | null;
  /** ISO timestamp of the last time the user opened this conversation —
   *  any assistant message newer than this counts as unread. */
  last_read_at?: string | null;
  /** Count of assistant messages newer than ``last_read_at``. Drives the
   *  sidebar badge; recomputed on each list/detail fetch and adjusted
   *  optimistically on the client when assistant_text streams arrive for
   *  a background conversation. */
  unread_count?: number;
  created_at: string;
  updated_at: string;
  /** ADR-0009 manager view — set by the backend ONLY when the caller is a
   *  manager opening a report's conversation. Drives the "working in
   *  {owner}'s conversation" banner; absent on the user's own chats. */
  viewer_is_manager?: boolean;
  owner_id?: string | null;
  owner_email?: string | null;
  owner_name?: string | null;
}

/** A user who reports (directly or indirectly) to the caller (ADR-0009). */
export interface ReportUser {
  id: string;
  email: string;
  name: string;
  avatar_url?: string | null;
}

/** A report's conversation row in the manager's Reports section — the sidebar
 *  list shape plus owner identity (every row is someone else's chat). */
export interface ReportConversation extends Conversation {
  owner_id: string;
  owner_email: string;
  owner_name: string;
}

/** One durable sub-agent run (ADR-0019, schema v40/v41).
 *
 *  ``status`` and ``result_seen`` answer different questions and the UI must
 *  show both: upstream reports background sub-agents dying early in 14-30% of
 *  runs while still reporting ``completed``, so a completed run that returned
 *  no result is a real and common outcome, not an edge case.
 *
 *  ``backgrounded`` is inferred (still running when its turn tore down), never
 *  reported by the SDK. ``orphaned`` means the process is gone — Stop does
 *  nothing to it; only Dismiss applies. */
export interface SubagentRun {
  tool_use_id: string;
  conversation_id: string;
  turn_id: string | null;
  environment: string | null;
  task_id: string | null;
  agent_type: string | null;
  description: string | null;
  status: "running" | "completed" | "failed" | "stopped" | "orphaned";
  last_tool_name: string | null;
  total_tokens: number;
  tool_uses: number;
  duration_ms: number;
  summary: string | null;
  result_seen: number;
  backgrounded: number;
  started_at: string;
  updated_at: string;
  ended_at: string | null;
}

/** Cumulative token totals forwarded on every result WS event. */
export interface TokenTotals {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
}

/** One model's cumulative token buckets within a conversation, plus the
 *  provider that served it (``"claude"``, ``"openrouter"``, ``"openai"``,
 *  ``"gemini"``, or ``"unknown"``). Provider ids map to display labels via
 *  ``providerLabel`` in TokenCounter. */
export interface ModelUsageEntry {
  provider: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
}

/** Per-model token breakdown for a conversation, keyed by the concrete model
 *  id that ran (e.g. ``"claude-opus-4-8"``, ``"openrouter/deepseek/..."``).
 *  Forwarded on every result WS event as the authoritative cumulative
 *  snapshot, and hydrated from the conversation row on open. */
export type ModelTokenUsage = Record<string, ModelUsageEntry>;

/** One slice of the context window, as the CLI categorises it. */
export interface ContextCategory {
  name: string;
  tokens: number;
  /** Progressive-disclosure schemas not yet loaded. These are NOT part of
   *  `total_tokens`, so they're shown as "would load", not as consumption. */
  deferred?: boolean;
}

/** The CLI's context-window measurement for the last turn — the same data
 *  `/context` shows, captured server-side via `get_context_usage()`.
 *
 *  Note on `max_tokens`: it is the CLI's ceiling, sized from the CLI's **own**
 *  model registry, which needn't know a proxied (OpenRouter) model's real
 *  window. The UI therefore pairs `total_tokens` with CoWork's own limit table
 *  rather than trusting this as the denominator — see `getContextLimit`. */
export interface ContextBreakdown {
  total_tokens: number;
  max_tokens: number;
  raw_max_tokens: number;
  model: string | null;
  categories: ContextCategory[];
  /** Per-MCP-tool token cost — what the tool manifest is charging us. */
  mcp_tools: Record<string, number>;
  measured_at: string;
}

/** Cumulative conversation spend (USD) forwarded on every result WS event.
 *  Absolute running totals (read back from the conversation row), so the
 *  footer cost counter is *set* rather than incremented — no double-count
 *  on replay and consistent with the value seeded on conversation open. */
export interface CostTotals {
  cost_usd: number;
  pplx_cost_usd: number;
}

export interface ConversationDetail extends Conversation {
  messages: Message[];
  /** True iff older messages exist on the server beyond the first page. */
  has_more_messages?: boolean;
  /** ``created_at`` of the oldest message in the returned page — pass it
   *  back as ``?before=`` to ``GET /conversations/{id}/messages``. */
  oldest_message_at?: string | null;
  /** True iff newer messages exist beyond the returned page — only ever set
   *  after an *anchored* open (``?anchor=``) lands the window mid-conversation;
   *  a normal open already holds the newest message. Drives "load newer". */
  has_more_newer?: boolean;
  /** ``created_at`` of the newest message in the returned page — pass it back
   *  as ``?after=`` to ``GET /conversations/{id}/messages`` to page down. */
  newest_message_at?: string | null;
  shortlist: ShortlistSummary | null;
  artifacts?: Array<{ id: string; filename: string; file_type: string; file_path?: string; created_at: string }>;
  /** Unresolved AskUserQuestion the agent is blocked on, if any.
   *  Hydrated on chat open so the clarification widget survives chat
   *  switches and app restarts — the in-memory request is lost on
   *  restart, but the row in ``pending_user_questions`` is not. */
  pending_question?: {
    request_id: string;
    questions: Array<{
      question: string;
      header: string;
      multiSelect: boolean;
      options: Array<{ label: string; description: string }>;
    }>;
  } | null;
}

/** Response shape for ``GET /conversations/{id}/messages``. */
export interface MessagesPage {
  messages: Message[];
  has_more: boolean;
  oldest_loaded: string | null;
  /** ``created_at`` of the newest message in the page — the cursor for the
   *  next forward (``?after=``) page. Present on forward pages. */
  newest_loaded?: string | null;
}

// --- Messages ---

export type MessageRole =
  | "user"
  | "assistant"
  | "system"
  | "artifact"
  | "genui"
  | "tool_use"
  | "tool_result"
  | "result"
  // The model's extended-thinking transcript, persisted as its own row so the
  // collapsed "Thought for Ns" disclosure sits above the answer it produced and
  // survives reload. Never replayed into the model's context (the backend's
  // _build_compacted_context only replays user/assistant/tool rows).
  | "thinking"
  // Inline "model switched to X by Y" notice, rendered as a centered timeline
  // divider (see MessageBubble). Persisted so the switch stays anchored in
  // history; never replayed into the model's own context (the backend's
  // _build_compacted_context only replays user/assistant/tool rows).
  | "model_switch"
  // Inline "environment switched to X by Y" notice — same centered-divider
  // treatment as model_switch, written when a conversation is moved between
  // execution environments (cloud ⇄ a device). Persisted + cross-device synced;
  // never replayed into the model's context.
  | "env_switch";

/** Sole content block of a ``model_switch`` message. */
export interface ModelSwitchBlock {
  type: "model_switch";
  /** Concrete model id that was switched to (e.g. ``claude-opus-4-8``). */
  model: string;
  /** Provider family at write time; the renderer re-derives display from ``model``. */
  provider?: string | null;
  /**
   * What triggered the switch. ``observer`` is retained only for historical
   * rows written by the now-removed conversation observer; new markers are
   * ``manual`` / ``auto`` / ``agent``.
   */
  source: "manual" | "auto" | "observer" | "agent";
  /** Human attribution shown in the notice ("Auto Router" or a person's name). */
  by: string;
  /** Optional why (e.g. the Auto Router's cost/route reason). */
  reason?: string | null;
}

/** Sole content block of an ``env_switch`` message. */
export interface EnvSwitchBlock {
  type: "env_switch";
  /** Raw execution_location ids (``cloud`` | ``desktop:<id>``). */
  from: string;
  to: string;
  /** Friendly names resolved at write time (device labels can't be re-derived
   *  on a client that doesn't know that device). */
  from_label: string;
  to_label: string;
  /** Human attribution shown in the notice (the person who moved it). */
  by: string;
}

/** Sole content block of a ``thinking`` message. */
export interface ThinkingContentBlock {
  type: "thinking";
  /** The model's reasoning transcript for the burst. */
  thinking: string;
  /** Elapsed thinking time in ms (drives the "Thought for Ns" label); null when
   *  the run streamed no deltas to measure against. */
  duration_ms?: number | null;
}

export interface Message {
  id: string;
  role: MessageRole;
  content_json: string;
  tool_name?: string;
  tool_use_id?: string;
  cost_usd?: number;
  pplx_cost_usd?: number;
  duration_ms?: number;
  // Which model produced this row. NULL for user-authored rows and for
  // any row written before the v15 schema migration — UI must render the
  // badge conditionally so historical messages don't show "unknown".
  model?: string | null;
  // The current user's RLHF vote on this (assistant) message, restored from
  // the server on reload. ``null``/absent means no vote yet. Mirrored to a
  // Langfuse ``user-feedback`` score + dataset item when set.
  feedback?: "like" | "dislike" | null;
  // The free-text "what went wrong" attached to the vote, if any.
  feedback_comment?: string | null;
  created_at: string;
  // Queued-send truth (#84, user rows only). ``client_msg_id`` is the id the
  // sending window assigned its optimistic bubble — every window dedups the
  // synced row against its local copy by it. ``sent_at`` is when the user SENT
  // the message; ``created_at`` is when the turn persisted it (minutes later
  // for a queued message) — the bubble timestamp prefers sent_at.
  client_msg_id?: string | null;
  sent_at?: string | null;
}

// --- WebSocket Client → Server ---

export type WsClientMessage =
  | { type: "subscribe"; conversation_id: string }
  | { type: "user_message"; conversation_id: string; content: string; model?: string; client_msg_id?: string }
  | { type: "approve_action"; conversation_id: string; action: string; scope?: "once" | "session" | "always"; request_id?: string }
  | { type: "deny_action"; conversation_id: string; request_id?: string }
  | { type: "interrupt"; conversation_id: string }
  | { type: "clear_queue"; conversation_id: string }
  | { type: "user_question_answer"; conversation_id: string; answers: Record<string, string>; request_id?: string }
  | { type: "genui_action"; conversation_id: string; payload: unknown };

// --- WebSocket Server → Client ---

export interface EvaluatorIssue {
  program: string;
  field: string;
  issue: string;
  severity: "critical" | "warning";
}

/** Intent of an attention-grabbing event — drives toast styling + urgency. */
export type AttentionIntent = "response" | "shortlist" | "question" | "approval" | "error";

export type WsServerMessage =
  // ``queued`` (present on subscribe/reconnect) re-hydrates the client's
  // optimistic "queued" bubbles from the server-side queue.
  | { type: "task_status"; conversation_id: string; running: boolean; queued?: string[] }
  // A message arrived while the agent was busy and was queued server-side.
  | { type: "message_queued"; conversation_id: string; client_msg_id?: string | null; content: string; queued: number }
  // A queued message has started running as the next turn — the client
  // promotes its optimistic "queued" bubble to a real user bubble.
  | { type: "message_dequeued"; conversation_id: string; client_msg_id?: string | null; content: string; queued: number }
  // The queued backlog was cleared (the live run is untouched).
  | { type: "queue_cleared"; conversation_id: string; dropped: number }
  | { type: "job_complete"; conversation_id: string; conversation_title: string; status: "success" | "error"; message: string; intent?: AttentionIntent; preview?: string }
  | { type: "assistant_text"; conversation_id: string; text: string; message_id: string; model?: string | null }
  // Cosmetic token partials; accumulated into a transient draft, discarded when the canonical assistant_text lands.
  | { type: "assistant_text_delta"; conversation_id: string; text: string; block: number }
  // Live extended-thinking deltas, shown in a collapsible panel during the run; not persisted.
  | { type: "thinking_delta"; conversation_id: string; text: string }
  // Canonical extended-thinking block — the persisted counterpart to the deltas
  // above. Lands right before ``assistant_text`` for the turn; the client appends
  // it as a ``thinking`` row (above the answer) and retires the transient panel.
  | { type: "thinking"; conversation_id: string; message_id: string; text: string; duration_ms: number | null }
  | { type: "tool_use_start"; conversation_id: string; tool_use_id: string; tool_name: string; input: Record<string, unknown> }
  | { type: "tool_result"; conversation_id: string; tool_use_id: string; content: string; is_error: boolean }
  // Live sub-agent (Task tool) lifecycle for the "Working" indicator. One frame
  // per state change, keyed by the main agent's ``Task`` tool_use_id (falls back
  // to task_id). ``status`` running→terminal drives the active count; the other
  // fields feed the per-sub-agent activity line (agent type, current tool,
  // tokens, elapsed). Cosmetic — not persisted; rebuilt each turn from the stream.
  | { type: "subagent_progress"; conversation_id: string; tool_use_id: string; agent_type: string | null; description: string | null; last_tool_name: string | null; total_tokens: number; tool_uses: number; duration_ms: number; status: "running" | "completed" | "failed" | "stopped" }
  | { type: "result"; conversation_id: string; session_id: string | null; cost_usd: number; pplx_cost_usd: number; duration_ms: number; num_turns: number; context_tokens?: number; context_percent?: number; context_model?: string | null; token_totals?: TokenTotals; cost_totals?: CostTotals; model_token_usage?: ModelTokenUsage; context_breakdown?: ContextBreakdown | null }
  | { type: "compact"; conversation_id: string; trigger?: string | null; pre_tokens?: number | null; model?: string | null; provider?: string | null }
  | { type: "shortlist_update"; conversation_id: string; shortlist_id: string; data: ShortlistData; artifacts?: Array<{ id: string; filename: string; file_type: string }> }
  | { type: "artifacts_update"; conversation_id: string; artifacts: Array<{ id: string; filename: string; file_type: string }> }
  | { type: "approval_request"; conversation_id: string; action: string; description: string; request_id?: string; intent?: AttentionIntent; preview?: string }
  | { type: "evaluator_running"; conversation_id: string }
  | { type: "evaluator_result"; conversation_id: string; passed: boolean; issues: EvaluatorIssue[]; feedback: string }
  // Live per-message token accounting emitted after each AssistantMessage
  // in a run. Deltas accumulate onto the running totals so the counter and
  // context nudge update turn-by-turn; the ``result`` event's
  // ``token_totals`` snapshot reconciles any drift at end-of-run.
  | { type: "token_update"; conversation_id: string; input_delta?: number; output_delta?: number; cache_read_delta?: number; cache_creation_delta?: number; context_tokens?: number; context_percent?: number; context_model?: string | null; provider?: string | null }
  | { type: "user_question"; conversation_id: string; request_id?: string; questions: Array<{ question: string; header: string; multiSelect: boolean; options: Array<{ label: string; description: string }> }>; intent?: AttentionIntent; preview?: string }
  | { type: "todos_updated"; conversation_id: string; tool_use_id?: string; todos: Array<{ content: string; status: "pending" | "in_progress" | "completed"; activeForm?: string }> }
  | { type: "genui"; conversation_id: string; genui_id: string; html: string; title?: string; file_path?: string }
  | { type: "genui_update"; conversation_id: string; genui_id: string; html: string }
  | { type: "error"; conversation_id: string; message: string }
  | { type: "focus_conversation"; conversation_id: string }
  | { type: "open_deeplink"; prompt: string }
  // Conversation-LIST live push (ADR-0008 Phase B2): a conversation created /
  // renamed / pinned / status-changed (upsert) or deleted in one of the user's
  // windows, pushed to the others so every sidebar stays in step.
  // ``owner_id`` says whose chat this is: the frame also reaches an authorized
  // cross-user viewer (ADR-0009), and only the client can tell whether the row
  // belongs in "Your Chats" or the admin "Others" tab. Optional so a frame from
  // an older backend still patches the owner's own list.
  | {
      type: "conversation_upsert";
      conversation_id: string;
      owner_id?: string;
      conversation: Conversation;
    }
  // Durable sub-agent runs for a conversation (ADR-0019). Carries the full row
  // set rather than a diff: the panel's truth is the table, and a client that
  // has to reconcile deltas is a client whose view can drift from it — which is
  // exactly how upstream's task panel resurrects stopped tasks as "Running".
  | { type: "subagents_changed"; conversation_id: string; subagents: SubagentRun[] }
  // A persisted role="system" notice (e.g. a finished delegation's receipt,
  // ADR-0019) — appended to the open transcript live so it shows without a
  // reload. ``tool_name``/``tool_use_id`` route it to the tool-style card.
  | {
      type: "system_notice";
      conversation_id: string;
      message_id: string;
      created_at: string;
      text: string;
      tool_name?: string | null;
      tool_use_id?: string | null;
    }
  | { type: "conversation_deleted"; conversation_id: string }
  // Real-time cloud→desktop sync (ADR-0008 Phase B4): a background pull applied
  // new messages to this conversation (it ran on the cloud). If it's the open
  // chat, the frontend refreshes its message tail live — no reload.
  | { type: "conversation_synced"; conversation_id: string }
  // Portable sessions (ADR-0008 Phase C): a turn was sent to a backend that does
  // NOT own this conversation's environment assignment, so it wasn't run here —
  // `location` is the environment it runs in, and the UI prompts a switch.
  | { type: "execution_elsewhere"; conversation_id: string; client_msg_id?: string | null; location: string }
  // Remote dispatch (#70): the message WAS relayed to the owning env, which is
  // running it now; the reply syncs back. Unlike execution_elsewhere (owner
  // offline → prompt), this keeps the optimistic bubble + "Working" state.
  | { type: "execution_dispatched"; conversation_id: string; client_msg_id?: string | null; location: string }
  // Launch-time `nbyula` CLI auto-update progress (within the pinned band).
  // ``found`` fires when a newer in-band release is detected and the download
  // starts; ``done`` when it finishes (``updated`` true iff the swap happened).
  // Not tied to a conversation — surfaced as a side toast. See
  // cowork.ui.nbyula_cli.run_startup_autoupdate.
  | { type: "cli_update"; phase: "found" | "done"; from_version: string | null; to_version: string | null; updated: boolean }
  // Cross-device backfill progress (ADR-0008), app-level. Surfaced as one
  // updatable toast that doubles as the "CoWork is now in the cloud" note.
  | { type: "sync_progress"; phase: "syncing" | "done" | "error"; done?: number; total?: number; conversations?: number; messages?: number }
  // Native only: the device's cloud credential is unrecoverable without a fresh
  // login. Triggers a full logout so the user re-signs-in and re-links it.
  | { type: "cloud_reauth_required" }
  | { type: "force_logout"; conversation_id: string; reason: string; message: string }
  // Auto routing + agent-initiated model switches.
  // ``model_switched`` fires when the ``switch_model`` MCP tool runs or
  // when the Auto resolver picks a non-default provider on the next turn.
  // ``provider_cooled_down`` fires when an SDK call surfaces a provider block
  // (429 / 529-overload / auth / OpenRouter-402 ``credit``) and the responsible
  // provider is marked unavailable; the same turn fails over to the other
  // provider, the next Auto pick skips the cooled one, and the model-switch
  // lock lifts so the user can switch manually.
  // ``by`` is the human attribution ("Auto Router"); ``message_id`` /
  // ``created_at`` identify the persisted ``model_switch`` marker row the
  // backend just wrote, so the live client renders it with a stable id and
  // timestamp that match what it'll get on reload.
  | { type: "model_switched"; conversation_id: string; model: string; provider: string | null; reason: string | null; source: "agent" | "auto"; by?: string; message_id?: string; created_at?: string }
  // Inline environment-switch marker (parity with ``model_switched``): a
  // conversation was moved between execution environments. Carries the persisted
  // ``env_switch`` row's id + timestamp so the live divider matches its durable
  // twin on reload.
  | { type: "env_switched"; conversation_id: string; from: string; to: string; from_label: string; to_label: string; by: string; message_id: string; created_at: string | null }
  | { type: "provider_cooled_down"; conversation_id: string; provider: string; kind: "rate_limit" | "overloaded" | "timeout" | "auth" | "credit"; reason: string };

// --- Cost breakdown (per-model, provider-reported) ---

/** One (model, provider) row of a conversation's cost breakdown.
 *  ``cost`` is null when the provider reported none — see ``cost_status``:
 *  ``settled`` (cost recorded), ``included`` (Claude flat-fee subscription),
 *  ``pending`` (streaming OpenRouter cost still reconciling), ``unavailable``. */
export interface CostBreakdownRow {
  model: string;
  provider: string | null;
  sources: string[]; // main_agent | fetch_and_verify | perplexity
  tokens: { input: number; output: number; cache_read: number; cache_write: number };
  cost: { total: number; input: number; output: number; cached: number } | null;
  cost_status: "settled" | "included" | "pending" | "unavailable";
}

export interface CostBreakdownResponse {
  rows: CostBreakdownRow[];
  total_usd: number;
  has_pending: boolean;
  has_included: boolean;
}

// --- Shortlists ---

export interface EligibilityGap {
  field: string;
  required_value: string;
  candidate_value: string;
  severity: string;
  notes?: string;
}

export interface Scholarship {
  name: string;
  amount: string;
}

export interface ProgramEvaluation {
  university_name?: string;
  program_name?: string;
  /** Short field names from base_prompt schema */
  university?: string;
  program?: string;
  country?: string;
  city?: string;
  program_url?: string;
  duration?: string;
  tuition?: string;
  intake?: string;
  application_deadline?: string | null;
  risk_level: "compliant" | "high_risk" | "hard_reject" | "unverified";
  eligibility_gaps?: EligibilityGap[];
  url_valid?: boolean;
  evidence_sources?: string[];
  scholarship?: Scholarship | null;
  // Fields for 'Why this rating?' which is shown in the shortlist.
  evidence_quote?: string;
  evidence_url?: string;
  recommendation_rationale?: string;
  recommendation?: string;
  notes?: string;
}

export interface CoverageStats {
  total_programs: number;
  compliant: number;
  high_risk: number;
  hard_reject: number;
  unverified: number;
  iterations_run?: number;
}

export interface ShortlistData {
  candidate_name: string;
  target_countries?: string[];
  target_field?: string;
  evaluations: ProgramEvaluation[];
  coverage_stats?: CoverageStats | null;
  research_summary?: string;
  search_queries_used?: string[];
}

export interface ShortlistSummary {
  id: string;
  candidate_name: string;
  version: number;
  updated_at: string;
}

export interface Shortlist extends ShortlistSummary {
  conversation_id: string;
  data: ShortlistData;
  created_at: string;
}

// --- Settings ---

export interface SettingValue {
  set: boolean;
  value?: string;
  masked?: string;
  error?: string;
}

export interface SettingsResponse {
  settings: Record<string, SettingValue>;
  setup_complete: boolean;
  /** True when served by the centrally-hosted cloud (vs the desktop app). */
  cloud_mode?: boolean;
  can_edit_global?: boolean;
}

// --- MCP Servers ---

export interface McpTool {
  name: string;
  description: string;
  enabled: boolean;
}

export interface McpServer {
  id: string;
  name: string;
  transport: "stdio" | "http" | "sse" | "sdk" | "mcpb";
  config: Record<string, unknown>;
  enabled: boolean;
  builtin: boolean;
  status: "connected" | "failed" | "unknown" | "disabled" | "running" | "requires_setup";
  status_msg: string | null;
  tools: McpTool[];
}

export interface McpServersResponse {
  servers: McpServer[];
}

// --- MCPB Extensions ---

export interface McpbUserConfigField {
  type: "string" | "number" | "boolean" | "directory" | "file";
  title: string;
  description?: string;
  required?: boolean;
  sensitive?: boolean;
  default?: string | number | boolean | string[];
  multiple?: boolean;
  min?: number;
  max?: number;
}

export interface McpbManifest {
  manifest_version?: string;
  name: string;
  display_name?: string;
  version: string;
  description: string;
  author?: { name: string; email?: string; url?: string };
  icon?: string;
  server: {
    type: string;
    entry_point?: string;
    mcp_config: {
      command: string;
      args?: string[];
      env?: Record<string, string>;
    };
  };
  user_config?: Record<string, McpbUserConfigField>;
  tools?: Array<{ name: string; description?: string }>;
  keywords?: string[];
  license?: string;
}

export interface McpbInstallResponse {
  manifest: McpbManifest;
  temp_dir: string;
}

// --- Tool call UI state ---

export type ToolCallStatus = "running" | "success" | "error";

export interface ToolCallState {
  tool_use_id: string;
  tool_name: string;
  input: Record<string, unknown>;
  status: ToolCallStatus;
  result?: string;
  is_error?: boolean;
  started_at: number;
  duration_ms?: number;
}

// --- Live sub-agent state (Working indicator) ---

/** One running (or just-finished) sub-agent spawned by the main agent via the
 *  ``Task`` tool. Populated live from ``subagent_progress`` frames and cleared
 *  at each turn boundary — never persisted. ``tool_use_id`` matches the Task
 *  tool-call card so a future drill-in can correlate them. */
export interface SubagentInfo {
  tool_use_id: string;
  conversation_id: string;
  /** Sub-agent type, e.g. ``research-scout`` (from TaskStarted's task_type). */
  agent_type: string | null;
  /** The task's description — the human-readable "what it's doing". */
  description: string | null;
  /** The tool the sub-agent is running right now (live, from progress ticks). */
  last_tool_name: string | null;
  total_tokens: number;
  tool_uses: number;
  duration_ms: number;
  status: "running" | "completed" | "failed" | "stopped";
  /** Client epoch ms first seen — feeds a smooth per-agent elapsed clock. */
  started_at: number;
}

// ─── Agent Skills ──────────────────────────────────────────────────────────

export type SkillScope = "bundled" | "installed";

export interface Skill {
  id: number;
  name: string;
  scope: SkillScope;
  path: string;
  description: string | null;
  source_type: string | null;
  source_url: string | null;
  source_subpath: string | null;
  commit_sha: string | null;
  enabled: boolean;
  requires_mcp: string[];
  errors: string[];
  installed_at: string;
  updated_at: string;
}

export interface SkillsListResponse {
  skills: Skill[];
}

export interface SkillExtraFile {
  name: string;
  size: number;
}

export interface SkillDetailResponse {
  skill: Skill;
  body: string;
  parsed_frontmatter: Record<string, unknown> | null;
  extra_files: SkillExtraFile[];
}

export interface SkillPreviewItem {
  name: string;
  description: string;
  relative_dir: string;
  requires_mcp: string[];
  errors: string[];
}

export interface SkillPreviewResponse {
  source: { type: string; url: string; subpath: string; display: string };
  commit_sha: string | null;
  skills: SkillPreviewItem[];
}

export interface SkillValidateResponse {
  errors: string[];
  valid: boolean;
}
